import { Question } from '../components/QuestionSection';

// v10.3.0 - Added sample comments to posts - 2026-03-17
export interface UserProfile {
  id: string;
  name: string;
  avatar?: string;
  country: 'Nigeria' | 'Ghana' | 'Kenya' | 'USA' | 'UK';
  trustScore: number;
  role: 'user' | 'agent' | 'admin' | 'business' | 'agency';
  badges: string[];
  isVerified: boolean;
}

export type ReportType = 'traffic' | 'weather' | 'event' | 'power' | 'fuel' | 'market' | 'security' | 'nuisance' | 'transport' | 'bus' | 'activity' | 'crime' | 'find' | 'water' | 'help';

export interface GlobalReport {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  userTrustScore: number;
  type: ReportType;
  subType?: string;
  content: string;
  location: string;
  region?: string; // State for Nigeria, City/Area for others
  country: 'Nigeria' | 'Ghana' | 'Kenya' | 'USA' | 'UK';
  timestamp: string;
  likes: number;
  comments: Question[];
  imageUrl?: string;
  metadata?: any; // For price, status, etc.
  // Repost fields
  isRepost?: boolean;
  originalPostId?: string;
  originalAuthor?: {
    userId: string;
    userName: string;
  };
  repostNote?: string; // User's comment when reposting
  repostCount?: number; // How many times this post has been reposted
}

// Nigerian name data for generating thousands of users
const NIGERIAN_FIRST_NAMES_MALE = [
  'Adekunle', 'Adewale', 'Adeyemi', 'Chukwudi', 'Chinedu', 'Chinonso', 'Chibueze', 'Emeka', 'Ebuka',
  'Ibrahim', 'Idris', 'Ismail', 'Kabiru', 'Musa', 'Mohammed', 'Mustapha', 'Abdullahi', 'Abubakar',
  'Oluwaseun', 'Olumide', 'Oluwatobi', 'Tobi', 'Tunde', 'Tayo', 'Segun', 'Seyi', 'Femi', 'Kunle',
  'Obinna', 'Obi', 'Okechukwu', 'Ikechukwu', 'Nnamdi', 'Uchenna', 'Kelechi', 'Chidi', 'Obiora',
  'Yusuf', 'Zakariya', 'Suleiman', 'Bashir', 'Hassan', 'Usman', 'Ahmad', 'Aliyu', 'Aminu',
  'Kehinde', 'Taiwo', 'Babatunde', 'Ayodeji', 'Bolaji', 'Damilola', 'Ayomide', 'Mayowa',
  'Chukwuma', 'Ifeanyi', 'Ugochukwu', 'Dubem', 'Kachi', 'Somtochukwu', 'Chisom', 'Nonso',
  'Abdul', 'Sani', 'Sadiq', 'Tijani', 'Nasir', 'Lukman', 'Kamilu', 'Jamiu'
];

const NIGERIAN_FIRST_NAMES_FEMALE = [
  'Amaka', 'Adaeze', 'Chioma', 'Chiamaka', 'Chinwe', 'Ngozi', 'Nneka', 'Ifeoma', 'Ebere',
  'Fatima', 'Aisha', 'Hauwa', 'Zainab', 'Halima', 'Maryam', 'Hadiza', 'Amina', 'Safiya',
  'Funke', 'Kemi', 'Yetunde', 'Bisola', 'Bukola', 'Folake', 'Omolara', 'Titilayo', 'Adeola',
  'Onyinye', 'Chidinma', 'Blessing', 'Chinaza', 'Chinyere', 'Ogechi', 'Amarachi', 'Uchenna',
  'Zainab', 'Rahma', 'Ruqayya', 'Sumayyah', 'Bilkisu', 'Jamila', 'Nafisa', 'Khadija',
  'Temitope', 'Oluwaseun', 'Oluwatosin', 'Ayomide', 'Moyosore', 'Olayinka', 'Omowunmi',
  'Nkechi', 'Ugochi', 'Chinenye', 'Oluebube', 'Adaora', 'Amarachi', 'Kosisochukwu', 'Somtochukwu'
];

const NIGERIAN_LAST_NAMES = [
  // Yoruba
  'Adeyemi', 'Akinwale', 'Ogunleye', 'Babatunde', 'Oluwole', 'Adeleke', 'Adebayo', 'Oladele',
  'Akinyemi', 'Oladipo', 'Adekunle', 'Oyewole', 'Afolabi', 'Ogundipe', 'Oyedepo', 'Ajayi',
  'Balogun', 'Okonkwo', 'Adeyinka', 'Olayinka', 'Ogunbiyi', 'Adebisi', 'Adeoye', 'Alabi',
  // Igbo
  'Okafor', 'Okonkwo', 'Eze', 'Nwankwo', 'Okeke', 'Nwachukwu', 'Obi', 'Igwe', 'Onyeka',
  'Nnaji', 'Anyanwu', 'Chukwu', 'Nwafor', 'Onuoha', 'Obiora', 'Ezeji', 'Okoye', 'Nnamdi',
  'Ugwu', 'Ike', 'Okorie', 'Iloh', 'Onyekwere', 'Chima', 'Ndukwe', 'Ezenwa',
  // Hausa
  'Ibrahim', 'Usman', 'Abubakar', 'Mohammed', 'Musa', 'Garba', 'Bello', 'Suleiman',
  'Yusuf', 'Abubakar', 'Abdullahi', 'Ahmad', 'Hassan', 'Aliyu', 'Khalid', 'Sadiq',
  // Others
  'Williams', 'Johnson', 'George', 'Peter', 'David', 'Samuel', 'Emmanuel', 'Daniel',
  'Akpan', 'Edet', 'Okon', 'Udoh', 'Bassey', 'Essien', 'Umoh', 'Udo'
];

const USER_BADGES = [
  'Elder', 'Quick Reporter', 'Traffic Expert', 'Power Tracker', 'Fuel Scout', 'Market Expert',
  'Weather Watcher', 'Road Warrior', 'Community Star', 'Rising Star', 'Newbie', 'Newbie Helper',
  'Top Contributor', 'Trusted Reporter', 'Guardian', 'Top Watchman', 'SafePay Merchant', 'Trusted Seller',
  'SafePay Pro', 'Top Seller', 'Market Vendor', 'Verified Seller', 'Transport Expert', 'FCT Guardian',
  'Garden City Watch', 'Coal City Watch', 'NEPA Insider', 'Market Watcher', 'Official', 
  'LASTMA Verified', 'FRSC Verified', 'Rivers State Rep', 'Community Elder'
];

// Nigerian States and Major Cities
const NIGERIAN_STATES_AND_CITIES = {
  'Lagos': ['Ikeja', 'Lekki', 'VI', 'Surulere', 'Yaba', 'Ikorodu', 'Ajah', 'Maryland', 'Festac', 'Ojota', 'Berger', 'Apapa'],
  'Abuja (FCT)': ['Wuse', 'Gwarinpa', 'Maitama', 'Garki', 'Kubwa', 'Asokoro', 'Jabi', 'Utako'],
  'Rivers': ['Port Harcourt', 'Eleme', 'Obio-Akpor', 'Eliozu', 'Rumuokoro', 'Garrison'],
  'Kano': ['Kano City', 'Sabon Gari', 'Nassarawa', 'Fagge', 'Ungogo'],
  'Oyo': ['Ibadan', 'Ogbomoso', 'Mokola', 'Bodija', 'Challenge'],
  'Enugu': ['Enugu', 'Nsukka', 'New Haven', 'Independence Layout', 'GRA'],
  'Kaduna': ['Kaduna', 'Zaria', 'Sabon Tasha', 'Barnawa'],
  'Edo': ['Benin City', 'Auchi', 'Ekpoma', 'Sapele Road'],
  'Delta': ['Warri', 'Asaba', 'Sapele', 'Ughelli'],
  'Anambra': ['Awka', 'Onitsha', 'Nnewi', 'Ekwulobia'],
  'Imo': ['Owerri', 'Orlu', 'Okigwe'],
  'Abia': ['Umuahia', 'Aba', 'Arochukwu'],
  'Akwa Ibom': ['Uyo', 'Ikot Ekpene', 'Eket'],
  'Cross River': ['Calabar', 'Ikom', 'Ogoja'],
  'Plateau': ['Jos', 'Bukuru', 'Shendam'],
  'Benue': ['Makurdi', 'Gboko', 'Otukpo'],
  'Kwara': ['Ilorin', 'Offa', 'Omu-Aran'],
  'Osun': ['Osogbo', 'Ile-Ife', 'Ilesa'],
  'Ogun': ['Abeokuta', 'Ijebu Ode', 'Sagamu', 'Ota']
} as const;

// Generate thousands of Nigerian users
function generateNigerianUsers(count: number): UserProfile[] {
  const users: UserProfile[] = [];
  
  for (let i = 0; i < count; i++) {
    const isMale = Math.random() > 0.5;
    const firstName = isMale 
      ? NIGERIAN_FIRST_NAMES_MALE[Math.floor(Math.random() * NIGERIAN_FIRST_NAMES_MALE.length)]
      : NIGERIAN_FIRST_NAMES_FEMALE[Math.floor(Math.random() * NIGERIAN_FIRST_NAMES_FEMALE.length)];
    const lastName = NIGERIAN_LAST_NAMES[Math.floor(Math.random() * NIGERIAN_LAST_NAMES.length)];
    
    // Generate trust score with realistic distribution
    const trustScore = Math.floor(Math.random() * 5000) + 100;
    
    // Determine role based on trust score
    let role: 'user' | 'agent' | 'business' | 'agency' = 'user';
    if (trustScore > 4000) role = Math.random() > 0.5 ? 'agency' : 'agent';
    else if (trustScore > 3000) role = Math.random() > 0.7 ? 'business' : 'user';
    else if (trustScore > 2500) role = Math.random() > 0.8 ? 'agent' : 'user';
    
    // Generate badges based on trust score and role
    const numBadges = trustScore > 3000 ? 3 : trustScore > 2000 ? 2 : trustScore > 1000 ? 1 : Math.random() > 0.5 ? 1 : 0;
    const badges: string[] = [];
    for (let j = 0; j < numBadges; j++) {
      const badge = USER_BADGES[Math.floor(Math.random() * USER_BADGES.length)];
      if (!badges.includes(badge)) badges.push(badge);
    }
    
    // Verification based on trust score
    const isVerified = trustScore > 2000 || role === 'agent' || role === 'agency' || role === 'business';
    
    users.push({
      id: `ung${1000 + i}`,
      name: `${firstName} ${lastName}`,
      country: 'Nigeria',
      trustScore,
      role,
      badges,
      isVerified
    });
  }
  
  return users;
}

// Report templates for different types
const REPORT_TEMPLATES = {
  traffic: [
    { subType: 'Heavy Go-Slow', templates: [
      '{location} is packed! Go-slow from here. {emoji}',
      'Heavy traffic at {location}. Moving like snail 🐌',
      'Total standstill at {location}. Find alternative route!',
      '{location} traffic is terrible right now. Plan ahead o!',
    ]},
    { subType: 'Free Flow', templates: [
      '{location} is clear! Moving smoothly. Enjoy! 😊',
      'No traffic at {location} this morning. Cruise through!',
      '{location} surprisingly clear today. Safe travels!',
    ]},
    { subType: 'Moderate', templates: [
      '{location} has light traffic. Moving steady.',
      'Small hold-up at {location} but flowing well.',
    ]}
  ],
  power: [
    { subType: 'Outage', templates: [
      'No light in {location} since {time}. NEPA why? 😭',
      'Total blackout in {location}. Generator noise everywhere!',
      'Light is out here in {location}. When will this end?',
    ]},
    { subType: 'Restored', templates: [
      '⚡ Up NEPA! Light just came back in {location}!',
      'Light is back in {location}! Thank God! 🙏',
      'Power restored in {location} after {time}. Charging everything now! 🔋',
    ]},
    { subType: 'Stable', templates: [
      '{location} has had steady light for {time}. Is this heaven? 😂',
      'Consistent power in {location}. Enjoying AC cool breeze! ❄️',
    ]}
  ],
  fuel: [
    { subType: 'Available', templates: [
      'Fuel at {location}! ₦{price} per liter. Queue is {queue}.',
      '{station} in {location} has PMS. Price is ₦{price}.',
      'Petrol available at {location}. ₦{price}/L. Rush before it finishes!',
    ]},
    { subType: 'Scarcity', templates: [
      'No fuel at {location}. All stations dry! 😫',
      '{location} area no get fuel anywhere. I don tire!',
      'Fuel scarcity hitting {location} hard. No station has fuel.',
    ]}
  ],
  market: [
    { subType: 'Price Update', templates: [
      '{item} now ₦{price} at {location}. {trend}',
      'Price alert! {item} is ₦{price} at {location} market.',
      '{item} price {trendWord} at {location}. Now ₦{price}.',
    ]}
  ],
  weather: [
    { subType: 'Rain', templates: [
      '🌧️ Heavy rainfall in {location} now! Find shelter!',
      'Rain alert for {location}! Carry umbrella o!',
      'Downpour in {location}. Roads are wet and slippery.',
    ]},
    { subType: 'Sunny', templates: [
      '☀️ Hot sun in {location} today! Stay hydrated.',
      'Scorching heat in {location}. Temperature is {temp}°C!',
    ]}
  ],
  event: [
    { subType: 'Community Event', templates: [
      '{eventName} happening at {location} {time}. Everyone is invited! 🎉',
      'Join us for {eventName} at {location} {time}. Don\'t miss out!',
      '{eventName} coming up at {location} {time}. See you there! 🎊',
    ]},
    { subType: 'Alert', templates: [
      'Important: {eventName} at {location} {time}. Please take note! ⚠️',
      'Heads up! {eventName} at {location} {time}. Plan accordingly.',
    ]}
  ],
  flood: [
    { subType: 'Flooding', templates: [
      'Heavy flooding at {location}! Water is {depth}. Avoid this area! 🌊',
      '{location} is completely flooded. Road impassable. Find alternative route!',
      'Flood alert for {location}. Water level is {depth}. Be careful! ⚠️',
      'Serious flooding at {location}. Cars are stuck! Don\'t come here! 🌊',
    ]},
    { subType: 'Cleared', templates: [
      'Flood water at {location} has receded. Road is now passable. ✅',
      '{location} flood cleared! You can pass now but drive carefully.',
    ]}
  ],
  find: [
    { subType: 'Lost Item', templates: [
      'Lost {item} around {location}. Please help me find it! 🙏',
      'Anyone seen {item} at {location}? Reward if found!',
      'Missing: {item} last seen at {location}. Contact me if found.',
    ]},
    { subType: 'Found Item', templates: [
      'Found {item} at {location}. Owner should contact me to claim it.',
      'I found {item} around {location}. Looking for the owner.',
    ]}
  ],
  nuisance: [
    { subType: 'Noise Pollution', templates: [
      'Excessive noise from {source} at {location}. Been going on for {time}! 😡',
      'Loud {source} disturbing everyone at {location}. Please something should be done!',
      'Noise pollution at {location} from {source}. Can\'t even sleep! 😫',
    ]},
    { subType: 'Environmental', templates: [
      'Waste dumping at {location}. The smell is terrible! 🤢',
      'Illegal dumping happening at {location}. Environmental hazard!',
      'Gutter overflow at {location}. Creating health issues! ⚠️',
    ]},
    { subType: 'Other', templates: [
      '{issue} at {location}. This needs attention! 🚨',
      'Public nuisance: {issue} at {location}. Very disturbing!',
    ]}
  ],
  water: [
    { subType: 'Water Vendor', templates: [
      'Clean water available at {location}! ₦{price} per {size}. Call {contact}.',
      'Water vendor at {location}. ₦{price} per {size}. Fast delivery! 💧',
      'Need water? Contact me at {location}. ₦{price} per {size}. Quality guaranteed!',
    ]},
    { subType: 'Shortage', templates: [
      'Water scarcity at {location}. No supply for {time}! 😭',
      'Tap water not running in {location} for {time}. We need help!',
    ]}
  ]
};

const TIMESTAMPS = ['2 mins ago', '5 mins ago', '10 mins ago', '15 mins ago', '20 mins ago', '30 mins ago', '45 mins ago', '1h ago', '2h ago', '3h ago', '4h ago', '5h ago', '6h ago', '8h ago', '12h ago', '1 day ago'];
const FUEL_STATIONS = ['NNPC', 'Mobil', 'Oando', 'Conoil', 'Total', 'MRS', 'Forte Oil', 'Rainoil', 'A.A. Rano'];
const QUEUE_STATUS = ['short', 'moderate', 'long', 'moving'];
const EVENT_NAMES = ['Town Hall Meeting', 'Community Cleanup', 'Street Market', 'Church Service', 'Mosque Prayer', 'Birthday Celebration', 'Wedding Ceremony', 'Naming Ceremony', 'Fundraiser', 'Sports Tournament'];
const LOST_ITEMS = ['iPhone', 'Samsung phone', 'wallet', 'car keys', 'handbag', 'laptop bag', 'ID card', 'ATM card', 'jewelry', 'documents'];
const NOISE_SOURCES = ['generator', 'loud music', 'construction work', 'night club', 'party', 'church program', 'mosque event'];
const NUISANCE_ISSUES = ['illegal parking', 'abandoned vehicle', 'street trading', 'blocked drainage'];
const WATER_SIZES = ['truck', 'tanker', 'jerrycan', '25L keg'];
const FLOOD_DEPTHS = ['knee-deep', 'waist-deep', 'ankle-deep', 'car-level'];

// Generate thousands of reports
function generateNigerianReports(count: number, allUsers: UserProfile[]): GlobalReport[] {
  const reports: GlobalReport[] = [];
  const states = Object.keys(NIGERIAN_STATES_AND_CITIES) as (keyof typeof NIGERIAN_STATES_AND_CITIES)[];
  const reportTypes: ('traffic' | 'power' | 'fuel' | 'market' | 'event' | 'flood' | 'find' | 'nuisance' | 'water')[] = 
    ['traffic', 'power', 'fuel', 'market', 'event', 'flood', 'find', 'nuisance', 'water'];
  
  // Filter Nigerian users only
  const nigerianUsers = allUsers.filter(u => u.country === 'Nigeria');
  
  for (let i = 0; i < count; i++) {
    const user = nigerianUsers[Math.floor(Math.random() * nigerianUsers.length)];
    const state = states[Math.floor(Math.random() * states.length)];
    const cities = NIGERIAN_STATES_AND_CITIES[state];
    const location = cities[Math.floor(Math.random() * cities.length)];
    const reportType = reportTypes[Math.floor(Math.random() * reportTypes.length)];
    const timestamp = TIMESTAMPS[Math.floor(Math.random() * TIMESTAMPS.length)];
    const likes = Math.floor(Math.random() * 1000);
    
    let content = '';
    let subType = '';
    let metadata: any = {};
    
    // Generate content based on report type
    if (reportType === 'traffic') {
      const trafficTypes = REPORT_TEMPLATES.traffic;
      const trafficType = trafficTypes[Math.floor(Math.random() * trafficTypes.length)];
      subType = trafficType.subType;
      const template = trafficType.templates[Math.floor(Math.random() * trafficType.templates.length)];
      content = template
        .replace('{location}', location)
        .replace('{emoji}', ['🚗', '🚦', '⚠️'][Math.floor(Math.random() * 3)]);
    } else if (reportType === 'power') {
      const powerTypes = REPORT_TEMPLATES.power;
      const powerType = powerTypes[Math.floor(Math.random() * powerTypes.length)];
      subType = powerType.subType;
      const template = powerType.templates[Math.floor(Math.random() * powerType.templates.length)];
      const duration = ['2 hours', '5 hours', '12 hours', '1 day', '2 days', '3 days'][Math.floor(Math.random() * 6)];
      content = template
        .replace('{location}', location)
        .replace('{time}', duration);
      metadata.status = subType.toLowerCase();
    } else if (reportType === 'fuel') {
      const fuelTypes = REPORT_TEMPLATES.fuel;
      const fuelType = fuelTypes[Math.floor(Math.random() * fuelTypes.length)];
      subType = fuelType.subType;
      const template = fuelType.templates[Math.floor(Math.random() * fuelType.templates.length)];
      const price = (Math.floor(Math.random() * 100) + 600).toString();
      const station = FUEL_STATIONS[Math.floor(Math.random() * FUEL_STATIONS.length)];
      const queue = QUEUE_STATUS[Math.floor(Math.random() * QUEUE_STATUS.length)];
      content = template
        .replace('{location}', location)
        .replace('{station}', station)
        .replace('{price}', price)
        .replace('{queue}', queue);
      if (subType === 'Available') {
        metadata.price = `₦${price}`;
        metadata.status = 'available';
      }
    } else if (reportType === 'market') {
      const items = [
        { name: 'Rice (50kg)', priceRange: [60000, 90000] },
        { name: 'Tomatoes (basket)', priceRange: [10000, 30000] },
        { name: 'Beans (congos)', priceRange: [5000, 15000] },
        { name: 'Onions (bag)', priceRange: [30000, 60000] },
        { name: 'Palm oil (25L)', priceRange: [35000, 55000] },
        { name: 'Garri (congo)', priceRange: [2000, 5000] },
        { name: 'Yam (tuber)', priceRange: [1500, 4000] },
        { name: 'Pepper (basket)', priceRange: [15000, 35000] },
        { name: 'Groundnut oil (4L)', priceRange: [8000, 15000] },
        { name: 'Chicken (live)', priceRange: [4000, 8000] },
        { name: 'Fresh fish (kg)', priceRange: [3000, 6000] },
        { name: 'Plantain (bunch)', priceRange: [1500, 3500] },
        { name: 'Egusi (cup)', priceRange: [800, 1500] },
        { name: 'Bread (loaf)', priceRange: [800, 1500] },
        { name: 'Groundnut (congo)', priceRange: [3000, 6000] },
        { name: 'Crayfish (cup)', priceRange: [1200, 2500] },
        { name: 'Egg (crate)', priceRange: [2500, 4000] },
        { name: 'Ginger (kg)', priceRange: [1500, 3000] },
        { name: 'Potato (bag)', priceRange: [25000, 45000] },
        { name: 'Corn (bag)', priceRange: [20000, 40000] },
      ];
      const item = items[Math.floor(Math.random() * items.length)];
      const price = Math.floor(Math.random() * (item.priceRange[1] - item.priceRange[0])) + item.priceRange[0];
      const trend = Math.random() > 0.5 ? 'up' : 'down';
      const trendWord = trend === 'up' ? 'increased' : 'decreased';
      subType = 'Price Update';
      const template = REPORT_TEMPLATES.market[0].templates[Math.floor(Math.random() * 3)];
      content = template
        .replace('{item}', item.name)
        .replace('{location}', location)
        .replace('{price}', price.toLocaleString())
        .replace('{trend}', trend === 'up' ? '📈' : '📉')
        .replace('{trendWord}', trendWord);
      metadata.price = `₦${price.toLocaleString()}`;
      metadata.item = item.name;
      metadata.trend = trend;
    } else if (reportType === 'weather') {
      const weatherTypes = REPORT_TEMPLATES.weather;
      const weatherType = weatherTypes[Math.floor(Math.random() * weatherTypes.length)];
      subType = weatherType.subType;
      const template = weatherType.templates[Math.floor(Math.random() * weatherType.templates.length)];
      const temp = Math.floor(Math.random() * 10) + 28;
      content = template
        .replace('{location}', location)
        .replace('{temp}', temp.toString());
    } else if (reportType === 'event') {
      const eventTypes = REPORT_TEMPLATES.event;
      const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
      subType = eventType.subType;
      const template = eventType.templates[Math.floor(Math.random() * eventType.templates.length)];
      const eventName = EVENT_NAMES[Math.floor(Math.random() * EVENT_NAMES.length)];
      const time = ['this Saturday', 'tomorrow', 'next week', 'this weekend', 'Friday 6pm'][Math.floor(Math.random() * 5)];
      content = template
        .replace('{eventName}', eventName)
        .replace('{location}', location)
        .replace('{time}', time);
      metadata.eventName = eventName;
      metadata.time = time;
    } else if (reportType === 'flood') {
      const floodTypes = REPORT_TEMPLATES.flood;
      const floodType = floodTypes[Math.floor(Math.random() * floodTypes.length)];
      subType = floodType.subType;
      const template = floodType.templates[Math.floor(Math.random() * floodType.templates.length)];
      const depth = FLOOD_DEPTHS[Math.floor(Math.random() * FLOOD_DEPTHS.length)];
      content = template
        .replace('{location}', location)
        .replace('{depth}', depth);
      metadata.depth = depth;
      metadata.status = subType.toLowerCase();
    } else if (reportType === 'find') {
      const findTypes = REPORT_TEMPLATES.find;
      const findType = findTypes[Math.floor(Math.random() * findTypes.length)];
      subType = findType.subType;
      const template = findType.templates[Math.floor(Math.random() * findType.templates.length)];
      const item = LOST_ITEMS[Math.floor(Math.random() * LOST_ITEMS.length)];
      content = template
        .replace('{item}', item)
        .replace('{location}', location);
      metadata.item = item;
    } else if (reportType === 'nuisance') {
      const nuisanceTypes = REPORT_TEMPLATES.nuisance;
      const nuisanceType = nuisanceTypes[Math.floor(Math.random() * nuisanceTypes.length)];
      subType = nuisanceType.subType;
      const template = nuisanceType.templates[Math.floor(Math.random() * nuisanceType.templates.length)];
      const duration = ['2 hours', '5 hours', '3 days', '1 week'][Math.floor(Math.random() * 4)];
      const source = NOISE_SOURCES[Math.floor(Math.random() * NOISE_SOURCES.length)];
      const issue = NUISANCE_ISSUES[Math.floor(Math.random() * NUISANCE_ISSUES.length)];
      content = template
        .replace('{location}', location)
        .replace('{time}', duration)
        .replace('{source}', source)
        .replace('{issue}', issue);
    } else if (reportType === 'water') {
      const waterTypes = REPORT_TEMPLATES.water;
      const waterType = waterTypes[Math.floor(Math.random() * waterTypes.length)];
      subType = waterType.subType;
      const template = waterType.templates[Math.floor(Math.random() * waterType.templates.length)];
      const price = (Math.floor(Math.random() * 3000) + 2000).toString();
      const size = WATER_SIZES[Math.floor(Math.random() * WATER_SIZES.length)];
      const contact = `080${Math.floor(Math.random() * 90000000) + 10000000}`;
      const duration = ['2 days', '1 week', '3 days'][Math.floor(Math.random() * 3)];
      content = template
        .replace('{location}', location)
        .replace('{price}', price)
        .replace('{size}', size)
        .replace('{contact}', contact)
        .replace('{time}', duration);
      if (subType === 'Water Vendor') {
        metadata.price = `₦${price}`;
        metadata.size = size;
        metadata.contact = contact;
      }
    }
    
    reports.push({
      id: `rng${5000 + i}`,
      userId: user.id,
      userName: user.name,
      userTrustScore: user.trustScore,
      type: reportType as ReportType,
      subType,
      content,
      location,
      region: state,
      country: 'Nigeria',
      timestamp,
      likes,
      comments: [],
      metadata
    });
  }
  
  return reports;
}

export const DUMMY_USERS: UserProfile[] = [
  // Original users
  { id: 'u1', name: 'Tunde Mike', country: 'Nigeria', trustScore: 1240, role: 'user', badges: ['Elder', 'Quick Reporter'], isVerified: true },
  { id: 'u2', name: 'Ama Konadu', country: 'Ghana', trustScore: 890, role: 'user', badges: ['Guardian'], isVerified: true },
  { id: 'u3', name: 'Juma K.', country: 'Kenya', trustScore: 1560, role: 'agent', badges: ['Top Watchman'], isVerified: true },
  { id: 'u4', name: 'Sarah J.', country: 'USA', trustScore: 450, role: 'user', badges: ['Newbie'], isVerified: false },
  { id: 'u5', name: 'James T.', country: 'UK', trustScore: 2100, role: 'admin', badges: ['Admin HQ'], isVerified: true },
  { id: 'u6', name: 'Fatima Y.', country: 'Nigeria', trustScore: 3200, role: 'agency', badges: ['LASTMA Verified'], isVerified: true },
  { id: 'u7', name: 'Kofi B.', country: 'Ghana', trustScore: 780, role: 'user', badges: ['Market Expert'], isVerified: true },
  { id: 'u8', name: 'Zainab M.', country: 'Nigeria', trustScore: 1100, role: 'user', badges: ['Power Tracker'], isVerified: true },
  
  // New Nigerian users - Lagos
  { id: 'u9', name: 'Chioma Okafor', country: 'Nigeria', trustScore: 2450, role: 'user', badges: ['Traffic Expert', 'Elder', 'Top Contributor'], isVerified: true },
  { id: 'u10', name: 'Adewale Balogun', country: 'Nigeria', trustScore: 1890, role: 'user', badges: ['Road Warrior', 'Quick Reporter'], isVerified: true },
  { id: 'u11', name: 'Ngozi Eze', country: 'Nigeria', trustScore: 3100, role: 'business', badges: ['Market Vendor', 'Trusted Seller'], isVerified: true },
  { id: 'u12', name: 'Ibrahim Yusuf', country: 'Nigeria', trustScore: 1560, role: 'user', badges: ['Fuel Scout'], isVerified: true },
  { id: 'u13', name: 'Blessing Okoro', country: 'Nigeria', trustScore: 890, role: 'user', badges: ['Rising Star'], isVerified: false },
  { id: 'u14', name: 'Adekunle Gold', country: 'Nigeria', trustScore: 4200, role: 'agent', badges: ['FRSC Verified', 'Official'], isVerified: true },
  { id: 'u15', name: 'Amaka Nwosu', country: 'Nigeria', trustScore: 2100, role: 'user', badges: ['Weather Watcher', 'Elder'], isVerified: true },
  { id: 'u16', name: 'Segun Ogunleye', country: 'Nigeria', trustScore: 670, role: 'user', badges: ['Newbie Helper'], isVerified: false },
  { id: 'u17', name: 'Kemi Adeyemi', country: 'Nigeria', trustScore: 1780, role: 'user', badges: ['Power Tracker', 'Community Star'], isVerified: true },
  { id: 'u18', name: 'Chukwudi Obi', country: 'Nigeria', trustScore: 3450, role: 'business', badges: ['SafePay Merchant', 'Trusted'], isVerified: true },
  
  // Abuja users
  { id: 'u19', name: 'Hauwa Garba', country: 'Nigeria', trustScore: 1950, role: 'user', badges: ['FCT Guardian', 'Quick Reporter'], isVerified: true },
  { id: 'u20', name: 'Emeka Okonkwo', country: 'Nigeria', trustScore: 2300, role: 'user', badges: ['Transport Expert'], isVerified: true },
  { id: 'u21', name: 'Aisha Bello', country: 'Nigeria', trustScore: 890, role: 'user', badges: ['Newbie'], isVerified: false },
  { id: 'u22', name: 'Daniel Akpan', country: 'Nigeria', trustScore: 1450, role: 'user', badges: ['Market Watcher'], isVerified: true },
  
  // Port Harcourt users
  { id: 'u23', name: 'Ogechi Williams', country: 'Nigeria', trustScore: 1670, role: 'user', badges: ['Garden City Watch'], isVerified: true },
  { id: 'u24', name: 'Chidi Nnamdi', country: 'Nigeria', trustScore: 920, role: 'user', badges: ['Fuel Scout'], isVerified: false },
  { id: 'u25', name: 'Ifeoma Okeke', country: 'Nigeria', trustScore: 2890, role: 'agent', badges: ['Rivers State Rep'], isVerified: true },
  
  // Kano users
  { id: 'u26', name: 'Suleiman Khalid', country: 'Nigeria', trustScore: 1100, role: 'user', badges: ['Market Expert'], isVerified: true },
  { id: 'u27', name: 'Fatima Abubakar', country: 'Nigeria', trustScore: 1540, role: 'user', badges: ['Community Elder'], isVerified: true },
  
  // Ibadan users
  { id: 'u28', name: 'Funke Adebayo', country: 'Nigeria', trustScore: 1230, role: 'user', badges: ['Traffic Reporter'], isVerified: true },
  { id: 'u29', name: 'Olumide Taiwo', country: 'Nigeria', trustScore: 780, role: 'user', badges: ['Rising Star'], isVerified: false },
  
  // Enugu users
  { id: 'u30', name: 'Chinonso Ike', country: 'Nigeria', trustScore: 1890, role: 'user', badges: ['Coal City Watch'], isVerified: true },
  { id: 'u31', name: 'Ebere Chukwu', country: 'Nigeria', trustScore: 2100, role: 'user', badges: ['Power Tracker', 'Elder'], isVerified: true },
  
  // Diverse mix
  { id: 'u32', name: 'Abdul Rahman', country: 'Nigeria', trustScore: 1450, role: 'user', badges: ['Fuel Scout'], isVerified: true },
  { id: 'u33', name: 'Chiamaka Nnaji', country: 'Nigeria', trustScore: 670, role: 'user', badges: ['Newbie'], isVerified: false },
  { id: 'u34', name: 'Yusuf Musa', country: 'Nigeria', trustScore: 3200, role: 'business', badges: ['Market Vendor', 'Verified Seller'], isVerified: true },
  { id: 'u35', name: 'Onyeka Igwe', country: 'Nigeria', trustScore: 1990, role: 'user', badges: ['Quick Reporter', 'Traffic Expert'], isVerified: true },
  { id: 'u36', name: 'Bisola Akinwale', country: 'Nigeria', trustScore: 2560, role: 'user', badges: ['Weather Watcher', 'Elder'], isVerified: true },
  { id: 'u37', name: 'Kehinde Ojo', country: 'Nigeria', trustScore: 890, role: 'user', badges: ['Rising Star'], isVerified: false },
  { id: 'u38', name: 'Nkechi Anyanwu', country: 'Nigeria', trustScore: 1780, role: 'user', badges: ['Community Star'], isVerified: true },
  { id: 'u39', name: 'Tobi Adeleke', country: 'Nigeria', trustScore: 1340, role: 'user', badges: ['Road Warrior'], isVerified: true },
  { id: 'u40', name: 'Halima Ibrahim', country: 'Nigeria', trustScore: 2890, role: 'agent', badges: ['NEPA Insider'], isVerified: true },
  { id: 'u41', name: 'Obiora Nwankwo', country: 'Nigeria', trustScore: 1120, role: 'user', badges: ['Market Watcher'], isVerified: false },
  { id: 'u42', name: 'Mariam Sanni', country: 'Nigeria', trustScore: 2340, role: 'user', badges: ['Elder', 'Trusted Reporter'], isVerified: true },
  { id: 'u43', name: 'Femi Oladele', country: 'Nigeria', trustScore: 980, role: 'user', badges: ['Newbie Helper'], isVerified: false },
  { id: 'u44', name: 'Ugochi Okafor', country: 'Nigeria', trustScore: 3450, role: 'business', badges: ['SafePay Pro', 'Top Seller'], isVerified: true },
  { id: 'u45', name: 'Rasheed Adebisi', country: 'Nigeria', trustScore: 1560, role: 'user', badges: ['Fuel Scout', 'Quick Reporter'], isVerified: true },
  
  // Generate 10,000+ more Nigerian users across all states
  ...generateNigerianUsers(10000)
];

export const DUMMY_REPORTS: GlobalReport[] = [
  // NIGERIA
  {
    id: 'r1',
    userId: 'u1',
    userName: 'Tunde Mike',
    userTrustScore: 1240,
    type: 'traffic',
    subType: 'Heavy Go-Slow',
    content: 'Third Mainland Bridge is at a total standstill towards Island. Use Eko Bridge instead.',
    location: 'Third Mainland Bridge',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '2 mins ago',
    likes: 156,
    comments: [
      {
        id: 'c1',
        user: 'Chioma A.',
        text: 'Thanks for the update! Is Eko Bridge clear now?',
        timestamp: '1 min ago',
        replies: []
      },
      {
        id: 'c2',
        user: 'Ahmed K.',
        text: 'I can confirm this. Just passed through there 5 minutes ago. Total gridlock!',
        timestamp: '30 secs ago',
        replies: []
      }
    ],
    repostCount: 12
  },
  // REPOST EXAMPLE 1: User reposted with commentary
  {
    id: 'repost-1',
    userId: 'u8',
    userName: 'Zainab M.',
    userTrustScore: 1100,
    type: 'traffic',
    subType: 'Heavy Go-Slow',
    content: 'Third Mainland Bridge is at a total standstill towards Island. Use Eko Bridge instead.',
    location: 'Third Mainland Bridge',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '5 mins ago',
    likes: 23,
    comments: [],
    isRepost: true,
    originalPostId: 'r1',
    originalAuthor: {
      userId: 'u1',
      userName: 'Tunde Mike'
    },
    repostNote: 'Still terrible as of 8:45 AM! Took alternative route via Eko Bridge and saved 30 minutes. Highly recommend!',
    repostCount: 0
  },
  {
    id: 'r2',
    userId: 'u8',
    userName: 'Zainab M.',
    userTrustScore: 1100,
    type: 'power',
    subType: 'Outage',
    content: 'Total blackout in Gbagada Phase 2 since 4 AM. Anyone has light nearby?',
    location: 'Gbagada Phase 2',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '15 mins ago',
    likes: 42,
    comments: [
      {
        id: 'c3',
        user: 'Fatima B.',
        text: 'Same here in Gbagada Phase 1. No light since yesterday night!',
        timestamp: '10 mins ago',
        replies: []
      },
      {
        id: 'c4',
        user: 'Emeka R.',
        text: 'Just called IBEDC. They said power will be restored by noon.',
        timestamp: '5 mins ago',
        replies: []
      },
      {
        id: 'c5',
        user: 'Blessing N.',
        text: 'Thank God for generator sha. This is too much!',
        timestamp: '2 mins ago',
        replies: []
      }
    ],
    metadata: { status: 'outage' },
    repostCount: 3
  },
  // REPOST EXAMPLE 2: User reposted without commentary
  {
    id: 'repost-2',
    userId: 'u10',
    userName: 'Chidi O.',
    userTrustScore: 850,
    type: 'power',
    subType: 'Outage',
    content: 'Total blackout in Gbagada Phase 2 since 4 AM. Anyone has light nearby?',
    location: 'Gbagada Phase 2',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '18 mins ago',
    likes: 8,
    comments: [],
    metadata: { status: 'outage' },
    isRepost: true,
    originalPostId: 'r2',
    originalAuthor: {
      userId: 'u8',
      userName: 'Zainab M.'
    },
    repostCount: 0
  },
  {
    id: 'r3',
    userId: 'u6',
    userName: 'LASTMA HQ',
    userTrustScore: 9999,
    type: 'traffic',
    subType: 'Official Alert',
    content: 'Security sweep ongoing at Berger. Expect delays on the service lane.',
    location: 'Berger Bus Stop',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 890,
    comments: [],
    metadata: { isOfficial: true },
    repostCount: 45
  },
  // GHANA
  {
    id: 'r4',
    userId: 'u2',
    userName: 'Ama Konadu',
    userTrustScore: 890,
    type: 'market',
    subType: 'Price Update',
    content: 'Yam prices at Makola Market have increased by 20% this week. GH₵ 50 per tuber now.',
    location: 'Makola Market',
    region: 'Accra',
    country: 'Ghana',
    timestamp: '30 mins ago',
    likes: 24,
    comments: [],
    metadata: { price: 'GH₵ 50', item: 'Yam', trend: 'up' }
  },
  {
    id: 'r5',
    userId: 'u7',
    userName: 'Kofi B.',
    userTrustScore: 780,
    type: 'transport',
    subType: 'Fuel Watch',
    content: 'Goil Station at Legon has short queues for petrol. Avoid Shell nearby.',
    location: 'Legon',
    region: 'Greater Accra',
    country: 'Ghana',
    timestamp: '2h ago',
    likes: 15,
    comments: [],
    metadata: { status: 'available', price: 'GH₵ 14.50' }
  },
  // KENYA
  {
    id: 'r6',
    userId: 'u3',
    userName: 'Juma K.',
    userTrustScore: 1560,
    type: 'traffic',
    subType: 'Gridlock',
    content: 'Mombasa Road is jammed due to an accident near Mlolongo. Slow moving.',
    location: 'Mombasa Road',
    region: 'Nairobi',
    country: 'Kenya',
    timestamp: '10 mins ago',
    likes: 89,
    comments: [],
    imageUrl: 'https://images.unsplash.com/photo-1545156521-77bd85671d30?auto=format&fit=crop&q=80&w=1000'
  },
  {
    id: 'r7',
    userId: 'u3',
    userName: 'Juma K.',
    userTrustScore: 1560,
    type: 'weather',
    subType: 'Rain',
    content: 'Heavy downpour in Westlands. Roads getting slippery. Stay safe.',
    location: 'Westlands',
    region: 'Nairobi',
    country: 'Kenya',
    timestamp: '1h ago',
    likes: 34,
    comments: []
  },
  // USA
  {
    id: 'r8',
    userId: 'u4',
    userName: 'Sarah J.',
    userTrustScore: 450,
    type: 'event',
    subType: 'Community Meet',
    content: 'Central Park gathering for neighborhood cleanup this Saturday!',
    location: 'Central Park',
    region: 'New York',
    country: 'USA',
    timestamp: '3h ago',
    likes: 120,
    comments: [],
    metadata: { date: 'Sat, Feb 21' }
  },
  {
    id: 'r9',
    userId: 'u4',
    userName: 'Sarah J.',
    userTrustScore: 450,
    type: 'market',
    subType: 'Gas Prices',
    content: 'Gas prices at Exxon down to $3.15. Best deal in the area.',
    location: 'Upper West Side',
    region: 'New York',
    country: 'USA',
    timestamp: '5h ago',
    likes: 12,
    comments: [],
    metadata: { price: '$3.15', item: 'Gas', trend: 'down' }
  },
  // UK
  {
    id: 'r10',
    userId: 'u5',
    userName: 'James T.',
    userTrustScore: 2100,
    type: 'transport',
    subType: 'Tube Delay',
    content: 'Central Line experiencing severe delays due to signal failure at Holborn.',
    location: 'Holborn Station',
    region: 'London',
    country: 'UK',
    timestamp: '5 mins ago',
    likes: 245,
    comments: [],
    metadata: { status: 'delayed', mode: 'Tube' }
  },
  {
    id: 'r12',
    userId: 'u3',
    userName: 'Hadiza K.',
    userTrustScore: 1800,
    type: 'traffic',
    subType: 'Moderate',
    content: 'Traffic is light near Wuse II market today. Moving well.',
    location: 'Wuse II',
    region: 'Abuja (FCT)',
    country: 'Nigeria',
    timestamp: '10 mins ago',
    likes: 45,
    comments: [],
    metadata: { state: 'Abuja' }
  },
  {
    id: 'r13',
    userId: 'u8',
    userName: 'Chidi O.',
    userTrustScore: 920,
    type: 'fuel',
    subType: 'Available',
    content: 'NNPC in Port Harcourt (Garrison) has PMS at official price. Short queue.',
    location: 'Garrison',
    region: 'Rivers',
    country: 'Nigeria',
    timestamp: '25 mins ago',
    likes: 67,
    comments: [],
    metadata: { price: '₦640', state: 'Rivers' }
  },
  {
    id: 'r14',
    userId: 'u1',
    userName: 'Suleiman K.',
    userTrustScore: 1100,
    type: 'market',
    subType: 'Price Alert',
    content: 'Food prices in Sabon Gari market are stabilizing. Bag of onions down to ₦45,000.',
    location: 'Sabon Gari',
    region: 'Kano',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 124,
    comments: [],
    metadata: { price: '₦45,000', item: 'Onions', trend: 'down', state: 'Kano' }
  },
  {
    id: 'r15',
    userId: 'u6',
    userName: 'Zainab M.',
    userTrustScore: 3200,
    type: 'power',
    subType: 'Restored',
    content: 'Light is back in Independence Layout! Hope it stays.',
    location: 'Independence Layout',
    region: 'Enugu',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 89,
    comments: [],
    metadata: { status: 'restored', state: 'Enugu' }
  },
  {
    id: 'r16',
    userId: 'u2',
    userName: 'Kweku A.',
    userTrustScore: 750,
    type: 'traffic',
    subType: 'Accident',
    content: 'Minor crash on Kumasi-Accra road near Ejisu. Traffic building up.',
    location: 'Ejisu',
    region: 'Ashanti',
    country: 'Ghana',
    timestamp: '5 mins ago',
    likes: 31,
    comments: []
  },
  {
    id: 'r-uk-fuel-1',
    userId: 'u5',
    userName: 'James T.',
    userTrustScore: 2100,
    type: 'fuel',
    subType: 'Available',
    content: 'BP station on Oxford St has plenty of fuel. No queues at all.',
    location: 'Oxford St',
    region: 'London',
    country: 'UK',
    timestamp: '10 mins ago',
    likes: 45,
    comments: [],
    metadata: { price: '£1.45/L', status: 'available' }
  },
  
  // === NEW ENGAGING NIGERIAN REPORTS ===
  
  // Lagos - Traffic & Transport
  {
    id: 'r100',
    userId: 'u9',
    userName: 'Chioma Okafor',
    userTrustScore: 2450,
    type: 'traffic',
    subType: 'Heavy Go-Slow',
    content: 'Lekki-Epe Expressway from Ajah to VGC is moving like snail 🐌. Better take alternative route. This traffic no be here o!',
    location: 'Lekki-Epe Expressway',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '5 mins ago',
    likes: 234,
    comments: [],
    imageUrl: 'https://images.unsplash.com/photo-1577086664693-894d8405334c?auto=format&fit=crop&q=80&w=1000'
  },
  {
    id: 'r101',
    userId: 'u10',
    userName: 'Adewale Balogun',
    userTrustScore: 1890,
    type: 'traffic',
    subType: 'Free Flow',
    content: 'Ikorodu Road is surprisingly clear this morning! Moving smoothly from Ketu to Maryland. Make una enjoy am while it lasts 😊',
    location: 'Ikorodu Road',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '18 mins ago',
    likes: 189,
    comments: []
  },
  {
    id: 'r102',
    userId: 'u35',
    userName: 'Onyeka Igwe',
    userTrustScore: 1990,
    type: 'traffic',
    subType: 'Accident',
    content: 'Accident at Ojuelegba under-bridge. One lane blocked. LASTMA officials are on ground. Please drive carefully!',
    location: 'Ojuelegba',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '12 mins ago',
    likes: 98,
    comments: [],
    imageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=1000'
  },
  {
    id: 'r103',
    userId: 'u39',
    userName: 'Tobi Adeleke',
    userTrustScore: 1340,
    type: 'transport',
    subType: 'Danfo Alert',
    content: 'Danfo buses at Yaba charging ₦300 to CMS now (used to be ₦200). Na wa o! Highway robbery in broad daylight.',
    location: 'Yaba Bus Stop',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '35 mins ago',
    likes: 156,
    comments: []
  },
  
  // Lagos - Power Updates
  {
    id: 'r104',
    userId: 'u17',
    userName: 'Kemi Adeyemi',
    userTrustScore: 1780,
    type: 'power',
    subType: 'Outage',
    content: 'Light is out here in Surulere since yesterday evening! NEPA, why now? Generator fuel is expensive o 😭',
    location: 'Surulere',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '22 mins ago',
    likes: 67,
    comments: [],
    metadata: { status: 'outage', duration: '18+ hours' }
  },
  {
    id: 'r105',
    userId: 'u40',
    userName: 'Halima Ibrahim',
    userTrustScore: 2890,
    type: 'power',
    subType: 'Restored',
    content: '⚡ Up NEPA! Light just came back in Ikeja GRA after 2 days. Thank God! Time to charge everything 🔋📱💻',
    location: 'Ikeja GRA',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '8 mins ago',
    likes: 234,
    comments: [],
    metadata: { status: 'restored' }
  },
  {
    id: 'r106',
    userId: 'u8',
    userName: 'Zainab M.',
    userTrustScore: 1100,
    type: 'power',
    subType: 'Stable',
    content: 'Festac Town has had steady light for the past 3 days! Is this a dream? Are we in heaven? 😂',
    location: 'Festac Town',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 445,
    comments: [],
    metadata: { status: 'stable', duration: '3 days' }
  },
  
  // Lagos - Fuel & Market
  {
    id: 'r107',
    userId: 'u12',
    userName: 'Ibrahim Yusuf',
    userTrustScore: 1560,
    type: 'fuel',
    subType: 'Available',
    content: 'Mobil at Falomo has fuel! ₦670 per liter. Queue is long but moving. Better rush before it finishes.',
    location: 'Falomo Roundabout',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '28 mins ago',
    likes: 178,
    comments: [],
    metadata: { price: '₦670', status: 'available', queue: 'long' }
  },
  {
    id: 'r108',
    userId: 'u45',
    userName: 'Rasheed Adebisi',
    userTrustScore: 1560,
    type: 'fuel',
    subType: 'Scarcity',
    content: 'All filling stations on Badagry Road are dry. No fuel anywhere. This scarcity is getting worse o!',
    location: 'Badagry Road',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '45 mins ago',
    likes: 89,
    comments: [],
    metadata: { status: 'unavailable' }
  },
  {
    id: 'r109',
    userId: 'u11',
    userName: 'Ngozi Eze',
    userTrustScore: 3100,
    type: 'market',
    subType: 'Price Update',
    content: '🍅 Tomatoes are cheaper at Mile 12 Market today! Big basket for ₦15,000 (was ₦25,000 last week). Stock up now!',
    location: 'Mile 12 Market',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 267,
    comments: [],
    metadata: { price: '₦15,000', item: 'Tomatoes (big basket)', trend: 'down' }
  },
  {
    id: 'r110',
    userId: 'u34',
    userName: 'Yusuf Musa',
    userTrustScore: 3200,
    type: 'market',
    subType: 'Price Alert',
    content: 'Bag of rice don increase again! ₦85,000 for foreign rice at Daleko Market. Wetin we no go see for this country 😢',
    location: 'Daleko Market',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 456,
    comments: [],
    metadata: { price: '₦85,000', item: 'Foreign Rice (50kg)', trend: 'up' }
  },
  
  // Lagos - Weather & Events
  {
    id: 'r111',
    userId: 'u15',
    userName: 'Amaka Nwosu',
    userTrustScore: 2100,
    type: 'weather',
    subType: 'Rain',
    content: '⛈️ Heavy rainfall in VI now! Roads are flooding. If you dey Victoria Island, find shelter. Thunder too much!',
    location: 'Victoria Island',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '3 mins ago',
    likes: 567,
    comments: [],
    imageUrl: 'https://images.unsplash.com/photo-1527766833261-b09c3163a791?auto=format&fit=crop&q=80&w=1000'
  },
  {
    id: 'r112',
    userId: 'u36',
    userName: 'Bisola Akinwale',
    userTrustScore: 2560,
    type: 'weather',
    subType: 'Sunny',
    content: '☀️ Hot sun in Mushin today! Make sure to drink plenty water and stay hydrated. Temperature is 34°C!',
    location: 'Mushin',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 123,
    comments: []
  },
  {
    id: 'r113',
    userId: 'u13',
    userName: 'Blessing Okoro',
    userTrustScore: 890,
    type: 'event',
    subType: 'Community Event',
    content: 'Free health screening at Ajegunle Primary Health Centre this Saturday! Blood pressure, sugar test, malaria test - all free. Come with your family!',
    location: 'Ajegunle',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '3h ago',
    likes: 234,
    comments: [],
    metadata: { date: 'Saturday, Mar 1', time: '9 AM - 4 PM' }
  },
  
  // Abuja Reports
  {
    id: 'r114',
    userId: 'u19',
    userName: 'Hauwa Garba',
    userTrustScore: 1950,
    type: 'traffic',
    subType: 'Moderate',
    content: 'Airport Road is building up towards Central Area. Leave early if you have appointment for city center.',
    location: 'Airport Road',
    region: 'Abuja (FCT)',
    country: 'Nigeria',
    timestamp: '15 mins ago',
    likes: 78,
    comments: []
  },
  {
    id: 'r115',
    userId: 'u20',
    userName: 'Emeka Okonkwo',
    userTrustScore: 2300,
    type: 'transport',
    subType: 'Taxi Alert',
    content: 'Taxis at Berger Roundabout are overcharging! ₦2,500 to Gwarinpa (normal price is ₦1,500). Use ride-hailing apps instead.',
    location: 'Berger Roundabout',
    region: 'Abuja (FCT)',
    country: 'Nigeria',
    timestamp: '40 mins ago',
    likes: 145,
    comments: []
  },
  {
    id: 'r116',
    userId: 'u21',
    userName: 'Aisha Bello',
    userTrustScore: 890,
    type: 'power',
    subType: 'Outage',
    content: 'No light in Kubwa since morning. Generator noise everywhere. When will this end? 😔',
    location: 'Kubwa',
    region: 'Abuja (FCT)',
    country: 'Nigeria',
    timestamp: '55 mins ago',
    likes: 56,
    comments: [],
    metadata: { status: 'outage' }
  },
  {
    id: 'r117',
    userId: 'u22',
    userName: 'Daniel Akpan',
    userTrustScore: 1450,
    type: 'market',
    subType: 'Price Update',
    content: 'Wuse Market prices are more reasonable today. Fresh chicken ₦3,500/kg. Beef ₦4,200/kg. Go check am out!',
    location: 'Wuse Market',
    region: 'Abuja (FCT)',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 189,
    comments: [],
    metadata: { items: 'Chicken ₦3,500/kg, Beef ₦4,200/kg' }
  },
  
  // Port Harcourt Reports
  {
    id: 'r118',
    userId: 'u23',
    userName: 'Ogechi Williams',
    userTrustScore: 1670,
    type: 'traffic',
    subType: 'Heavy Go-Slow',
    content: 'Aba Road from Rumuokoro to Garrison is packed! Friday rush hour traffic is real. Plan ahead o!',
    location: 'Aba Road',
    region: 'Rivers',
    country: 'Nigeria',
    timestamp: '20 mins ago',
    likes: 92,
    comments: []
  },
  {
    id: 'r119',
    userId: 'u24',
    userName: 'Chidi Nnamdi',
    userTrustScore: 920,
    type: 'fuel',
    subType: 'Available',
    content: 'Oando at Eliozu has fuel selling at ₦650. Queue is not too long. Better rush!',
    location: 'Eliozu',
    region: 'Rivers',
    country: 'Nigeria',
    timestamp: '32 mins ago',
    likes: 124,
    comments: [],
    metadata: { price: '₦650', status: 'available' }
  },
  {
    id: 'r120',
    userId: 'u25',
    userName: 'Ifeoma Okeke',
    userTrustScore: 2890,
    type: 'weather',
    subType: 'Rain',
    content: '🌧️ Rain alert! Heavy downpour expected in PH this evening according to NIMET. Carry umbrella o!',
    location: 'Port Harcourt',
    region: 'Rivers',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 234,
    comments: [],
    metadata: { source: 'NIMET', isOfficial: true }
  },
  
  // Kano Reports
  {
    id: 'r121',
    userId: 'u26',
    userName: 'Suleiman Khalid',
    userTrustScore: 1100,
    type: 'market',
    subType: 'Price Update',
    content: 'Groundnut oil price don crash for Sabon Gari Market! 25 liters for ₦45,000. This is good news!',
    location: 'Sabon Gari Market',
    region: 'Kano',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 178,
    comments: [],
    metadata: { price: '₦45,000', item: 'Groundnut Oil (25L)', trend: 'down' }
  },
  {
    id: 'r122',
    userId: 'u27',
    userName: 'Fatima Abubakar',
    userTrustScore: 1540,
    type: 'power',
    subType: 'Stable',
    content: 'Light has been stable in Nassarawa GRA for past week. Alhamdulillah! Enjoying cool breeze from AC 😊',
    location: 'Nassarawa GRA',
    region: 'Kano',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 267,
    comments: [],
    metadata: { status: 'stable', duration: '1 week' }
  },
  
  // Ibadan Reports
  {
    id: 'r123',
    userId: 'u28',
    userName: 'Funke Adebayo',
    userTrustScore: 1230,
    type: 'traffic',
    subType: 'Moderate',
    content: 'Mokola Roundabout get small hold-up but e dey flow steady. If you wan reach Challenge, use Ring Road instead.',
    location: 'Mokola',
    region: 'Oyo',
    country: 'Nigeria',
    timestamp: '25 mins ago',
    likes: 89,
    comments: []
  },
  {
    id: 'r124',
    userId: 'u29',
    userName: 'Olumide Taiwo',
    userTrustScore: 780,
    type: 'fuel',
    subType: 'Scarcity',
    content: 'Bodija area no get fuel anywhere. All stations dry. I don tire to dey look sef 😫',
    location: 'Bodija',
    region: 'Oyo',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 56,
    comments: [],
    metadata: { status: 'unavailable' }
  },
  
  // Enugu Reports
  {
    id: 'r125',
    userId: 'u30',
    userName: 'Chinonso Ike',
    userTrustScore: 1890,
    type: 'traffic',
    subType: 'Free Flow',
    content: 'Enugu-Onitsha Expressway clear as water! Moving smoothly from 9th Mile to Opi Junction. Safe travels!',
    location: 'Enugu-Onitsha Expressway',
    region: 'Enugu',
    country: 'Nigeria',
    timestamp: '35 mins ago',
    likes: 134,
    comments: []
  },
  {
    id: 'r126',
    userId: 'u31',
    userName: 'Ebere Chukwu',
    userTrustScore: 2100,
    type: 'power',
    subType: 'Outage',
    content: 'New Haven area without light for 3 days now. EEDC, please do something! Business owners are suffering.',
    location: 'New Haven',
    region: 'Enugu',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 198,
    comments: [],
    metadata: { status: 'outage', duration: '3 days' }
  },
  
  // More Lagos - Security & Community
  {
    id: 'r127',
    userId: 'u14',
    userName: 'Adekunle Gold',
    userTrustScore: 4200,
    type: 'security',
    subType: 'Official Alert',
    content: '🚨 FRSC ALERT: Speed limit enforcement ongoing on Lagos-Ibadan Expressway. Drive within 100km/h. Stay safe! 🚦',
    location: 'Lagos-Ibadan Expressway',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 567,
    comments: [],
    metadata: { isOfficial: true, source: 'FRSC' }
  },
  {
    id: 'r128',
    userId: 'u38',
    userName: 'Nkechi Anyanwu',
    userTrustScore: 1780,
    type: 'event',
    subType: 'Market Day',
    content: 'Big discount sale at Computer Village today! Laptop prices slashed by 15%. Perfect time to upgrade your gadgets 💻📱',
    location: 'Computer Village, Ikeja',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '3h ago',
    likes: 445,
    comments: [],
    metadata: { discount: '15%', items: 'Laptops, Phones, Accessories' }
  },
  {
    id: 'r129',
    userId: 'u18',
    userName: 'Chukwudi Obi',
    userTrustScore: 3450,
    type: 'market',
    subType: 'SafePay Deal',
    content: '🛡️ Selling 2 brand new generators (5KVA). ₦280,000 each. SafePay protected transaction. DM for details!',
    location: 'Alaba International Market',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '4h ago',
    likes: 234,
    comments: [],
    metadata: { price: '₦280,000', item: 'Generator 5KVA', safepay: true }
  },
  {
    id: 'r130',
    userId: 'u42',
    userName: 'Mariam Sanni',
    userTrustScore: 2340,
    type: 'nuisance',
    subType: 'Noise Complaint',
    content: 'Loud party in Magodo Phase 2 since 10 PM. Music too loud, neighbors can\'t sleep. Please respect others o! 🔊😴',
    location: 'Magodo Phase 2',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '30 mins ago',
    likes: 89,
    comments: []
  },
  
  // Mixed engaging reports
  {
    id: 'r131',
    userId: 'u32',
    userName: 'Abdul Rahman',
    userTrustScore: 1450,
    type: 'fuel',
    subType: 'Available',
    content: 'Conoil at Ajao Estate selling fuel ₦665. Queue is manageable. If you need fuel, come quick!',
    location: 'Ajao Estate',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '48 mins ago',
    likes: 156,
    comments: [],
    metadata: { price: '₦665', status: 'available', queue: 'moderate' }
  },
  {
    id: 'r132',
    userId: 'u33',
    userName: 'Chiamaka Nnaji',
    userTrustScore: 670,
    type: 'transport',
    subType: 'Bus Alert',
    content: 'BRT buses at Ikorodu garage are scarce this morning. Long queue of people waiting. Consider alternative transport.',
    location: 'Ikorodu Garage',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '1h ago',
    likes: 78,
    comments: []
  },
  {
    id: 'r133',
    userId: 'u37',
    userName: 'Kehinde Ojo',
    userTrustScore: 890,
    type: 'weather',
    subType: 'Windy',
    content: 'Strong wind in Lekki Phase 1. Trees are swaying heavily. Secure loose items outside your homes! 🌬️',
    location: 'Lekki Phase 1',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '22 mins ago',
    likes: 167,
    comments: []
  },
  {
    id: 'r134',
    userId: 'u41',
    userName: 'Obiora Nwankwo',
    userTrustScore: 1120,
    type: 'market',
    subType: 'Price Alert',
    content: 'Cement price increase alert! Dangote cement now ₦6,800 per bag at Apapa. Up from ₦5,900 last month 📈',
    location: 'Apapa',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '3h ago',
    likes: 298,
    comments: [],
    metadata: { price: '₦6,800', item: 'Cement (Dangote)', trend: 'up' }
  },
  {
    id: 'r135',
    userId: 'u43',
    userName: 'Femi Oladele',
    userTrustScore: 980,
    type: 'event',
    subType: 'Community Alert',
    content: 'Community clean-up exercise at Maryland tomorrow morning! Bring brooms and waste bags. Let\'s keep our environment clean 🧹🌿',
    location: 'Maryland',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '5h ago',
    likes: 234,
    comments: [],
    metadata: { date: 'Tomorrow', time: '7 AM' }
  },
  {
    id: 'r136',
    userId: 'u44',
    userName: 'Ugochi Okafor',
    userTrustScore: 3450,
    type: 'market',
    subType: 'SafePay Deal',
    content: '🎉 Flash Sale! iPhone 14 Pro Max (256GB) for ₦850,000. Original, with warranty. SafePay escrow available. Limited stock!',
    location: 'Ikeja City Mall',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '2h ago',
    likes: 678,
    comments: [],
    metadata: { price: '₦850,000', item: 'iPhone 14 Pro Max 256GB', safepay: true, warranty: true }
  },
  {
    id: 'r137',
    userId: 'u16',
    userName: 'Segun Ogunleye',
    userTrustScore: 670,
    type: 'power',
    subType: 'Restored',
    content: 'Light just came back to Alapere! After 12 hours of darkness. Thank you Jesus! 🙏⚡',
    location: 'Alapere',
    region: 'Lagos',
    country: 'Nigeria',
    timestamp: '10 mins ago',
    likes: 145,
    comments: [],
    metadata: { status: 'restored' }
  },
  
  // Generate 10,000+ diverse reports from Nigerian users across all states
  // Includes traffic, power, fuel, market, events, floods, lost/found, nuisance, and water reports
  ...generateNigerianReports(10000, DUMMY_USERS)
];